import React, { useState } from "react";
import LoadingRow from "./LoadingRow";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import PendingDataNotice from "./PendingDataNotice";
import "./styles/tableStyles.css";

// Verdict bands — highest threshold first. Mirrors the mockup's thresholds.
const VERDICTS = [
    { min: 0.12, label: "Blessed", chip: "bg-green-light text-[#15543a]" },
    { min: 0.04, label: "Lucky", chip: "bg-[#e7f5ec] text-green" },
    { min: -0.04, label: "Even", chip: "bg-[#efece2] text-text-mid" },
    { min: -0.12, label: "Unlucky", chip: "bg-[#fce6e1] text-[#a23a29]" },
    { min: -Infinity, label: "Robbed", chip: "bg-coral-light text-[#a23a29]" },
];

const getVerdict = (value) =>
    VERDICTS.find((band) => value >= band.min) || VERDICTS[VERDICTS.length - 1];

const POS_BAR = "linear-gradient(90deg,#52b788,#2d6a4f)";
const NEG_BAR = "linear-gradient(90deg,#c94f3a,#e07a68)";

const formatPct = (value) => `${value >= 0 ? "+" : ""}${(value * 100).toFixed(1)}%`;

// A single factor row inside the expanded waterfall.
const WaterfallRow = ({ factor, scaleMax }) => {
    const positive = factor.value >= 0;
    const width = scaleMax ? (Math.abs(factor.value) / scaleMax) * 48 : 0;
    const isZero = Math.abs(factor.value) < 0.001;
    const valueClass = isZero
        ? "text-text-muted"
        : positive
        ? "text-green"
        : "text-coral";

    return (
        <div className="grid grid-cols-[130px_1fr_48px] sm:grid-cols-[200px_1fr_60px] gap-3 items-center mb-2">
            <div className="text-left text-[0.78rem] font-bold leading-tight">
                <span className="mr-1.5">{factor.emoji}</span>
                {factor.label}
                {factor.blurb && (
                    <small className="block text-[0.66rem] font-semibold text-text-muted mt-0.5">
                        {factor.blurb}
                    </small>
                )}
            </div>
            <div className="relative h-[18px] rounded bg-[#f1eee4]">
                <div
                    className="absolute -top-[3px] -bottom-[3px] w-px bg-[#c9c3b4]"
                    style={{ left: "50%" }}
                />
                <div
                    className="absolute top-0.5 h-[14px] rounded-[3px]"
                    style={{
                        [positive ? "left" : "right"]: "50%",
                        width: `${width}%`,
                        background: positive ? "#52b788" : "#c94f3a",
                    }}
                />
            </div>
            <div
                className={`font-mono font-bold text-[0.78rem] text-right ${valueClass}`}
            >
                {positive ? "+" : ""}
                {(factor.value * 100).toFixed(1)}
            </div>
        </div>
    );
};

const LuckIndexTable = ({ data, week, loading, nCompletedWeeks }) => {
    const [openRows, setOpenRows] = useState(() => new Set());

    const toggleRow = (index) => {
        setOpenRows((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const maxAbs =
        data && data.length
            ? Math.max(...data.map((team) => Math.abs(team.value ?? 0)), 0.001)
            : 0.001;

    return (
        <CopyableContainer
            title={
                <LeagueTabHeading
                    eyebrow="Luck Index"
                    title={`Week ${week}`}
                    className="mb-0"
                />
            }
            fileName={`luck-index-week-${week}`}
        >
            <PendingDataNotice
                dataType="Luck Index"
                isPending={week > nCompletedWeeks}
            />
            <p className="mb-3 text-sm font-semibold text-text-muted">
                The week's fortune ledger, ranked luckiest to most robbed. Expand
                any team for a waterfall of every factor that added or subtracted
                from their score.
            </p>
            <div className="w-full overflow-x-auto overflow-y-hidden rounded-card border border-border-card bg-ds-white shadow-card">
                <table className="min-w-[680px] w-full border-collapse">
                    <thead>
                        <tr className="bg-forest text-[0.62rem] font-extrabold uppercase tracking-[0.09em] text-white/80">
                            <th className="px-3 py-2.5 text-center">#</th>
                            <th className="px-3 py-2.5 text-left">Owner</th>
                            <th className="px-3 py-2.5">
                                <div className="flex items-center justify-between text-[0.58rem] tracking-[0.05em]">
                                    <span className="text-[#ffb4a6]">
                                        ◄ Unlucky
                                    </span>
                                    <span className="text-[#9fe0bb]">
                                        Lucky ►
                                    </span>
                                </div>
                            </th>
                            <th className="px-3 py-2.5 text-center">Verdict</th>
                            <th className="w-8 px-3 py-2.5" aria-label="Expand" />
                        </tr>
                    </thead>
                    <tbody>
                        {loading || !data ? (
                            <LoadingRow
                                text="Calculating Luck Index..."
                                colSpan="5"
                            />
                        ) : data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan="5"
                                    className="px-3 py-4 text-center text-text-muted"
                                >
                                    No luck index data available.
                                </td>
                            </tr>
                        ) : (
                            data.map((team, index) => {
                                const value = team.value ?? 0;
                                const positive = value >= 0;
                                const pct = (Math.abs(value) / maxAbs) * 48;
                                const verdict = getVerdict(value);
                                // Sort factors most luck gained → most lost,
                                // rather than the fixed factor order.
                                const factors = [...(team.factors || [])].sort(
                                    (a, b) => b.value - a.value
                                );
                                const hasFactors = factors.length > 0;
                                const isOpen = openRows.has(index);
                                const wfMax = hasFactors
                                    ? Math.max(
                                          ...factors.map((f) =>
                                              Math.abs(f.value)
                                          )
                                      )
                                    : 0;

                                return (
                                    <React.Fragment key={index}>
                                        <tr
                                            className={`border-b border-[#efece2] align-middle transition-colors ${
                                                hasFactors
                                                    ? "cursor-pointer hover:bg-green-light"
                                                    : ""
                                            }`}
                                            onClick={
                                                hasFactors
                                                    ? () => toggleRow(index)
                                                    : undefined
                                            }
                                        >
                                            <td className="px-3 py-3 text-center">
                                                <span
                                                    className={`font-display text-base font-black ${
                                                        index < 3
                                                            ? "text-mustard"
                                                            : "text-text-muted"
                                                    }`}
                                                >
                                                    {index + 1}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-left">
                                                <span className="team-identity-primary">
                                                    {team.owner || team.team}
                                                </span>
                                                {team.owner && team.team ? (
                                                    <span className="team-identity-secondary">
                                                        {team.team}
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td className="min-w-[210px] px-3 py-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="relative h-[18px] min-w-[110px] flex-1">
                                                        <div className="absolute -top-0.5 -bottom-0.5 left-1/2 w-px bg-border" />
                                                        <div
                                                            className="absolute top-0.5 h-[14px] rounded-[3px]"
                                                            style={{
                                                                [positive
                                                                    ? "left"
                                                                    : "right"]:
                                                                    "50%",
                                                                width: `${pct}%`,
                                                                backgroundImage: positive
                                                                    ? POS_BAR
                                                                    : NEG_BAR,
                                                            }}
                                                        />
                                                    </div>
                                                    <div
                                                        className={`min-w-[54px] text-right font-mono text-[0.85rem] font-bold ${
                                                            positive
                                                                ? "text-green"
                                                                : "text-coral"
                                                        }`}
                                                    >
                                                        {formatPct(value)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-center">
                                                <span
                                                    className={`inline-block rounded-full px-2 py-1 text-[0.62rem] font-black uppercase tracking-[0.06em] ${verdict.chip}`}
                                                >
                                                    {verdict.label}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-center">
                                                {hasFactors && (
                                                    <span
                                                        className="inline-block text-text-muted transition-transform"
                                                        style={{
                                                            transform: isOpen
                                                                ? "rotate(90deg)"
                                                                : "none",
                                                        }}
                                                    >
                                                        ▶
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                        {hasFactors && isOpen && (
                                            <tr className="border-b border-[#efece2] bg-[#fbf9f3]">
                                                <td colSpan="5" className="p-0">
                                                    <div className="px-5 pb-6 pt-4 sm:px-6">
                                                        <div className="mb-4 text-[0.66rem] font-extrabold uppercase tracking-[0.09em] text-text-muted">
                                                            Factor waterfall — how{" "}
                                                            <b className="text-forest">
                                                                {team.owner || team.team}
                                                            </b>{" "}
                                                            reached{" "}
                                                            {formatPct(value)}
                                                        </div>
                                                        <div>
                                                            {factors.map(
                                                                (factor, fi) => (
                                                                    <WaterfallRow
                                                                        key={fi}
                                                                        factor={
                                                                            factor
                                                                        }
                                                                        scaleMax={
                                                                            wfMax
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                            <div className="mt-3 grid grid-cols-[130px_1fr_48px] items-center gap-3 border-t-2 border-[#e3dfd2] pt-3 sm:grid-cols-[200px_1fr_60px]">
                                                                <div className="font-display text-left font-black">
                                                                    Overall Luck
                                                                    Index
                                                                </div>
                                                                <div className="relative h-[18px] rounded bg-[#f1eee4]">
                                                                    <div
                                                                        className="absolute -top-[3px] -bottom-[3px] w-px bg-[#c9c3b4]"
                                                                        style={{
                                                                            left: "50%",
                                                                        }}
                                                                    />
                                                                    <div
                                                                        className="absolute top-0.5 h-[14px] rounded-[3px]"
                                                                        style={{
                                                                            [positive
                                                                                ? "left"
                                                                                : "right"]:
                                                                                "50%",
                                                                            width: `${pct}%`,
                                                                            background: positive
                                                                                ? "#52b788"
                                                                                : "#c94f3a",
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div
                                                                    className={`text-right font-display text-[0.95rem] font-black ${
                                                                        positive
                                                                            ? "text-green"
                                                                            : "text-coral"
                                                                    }`}
                                                                >
                                                                    {formatPct(
                                                                        value
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </CopyableContainer>
    );
};

export default LuckIndexTable;
